// Spoiler-free share card: no artwork, only the "restoration path" — how
// quickly each piece found its place — plus time, hints and the streak.

const W = 1080;
const H = 1350;
const INK = "#07101f";
const PAPER = "#f2eee4";
const GOLD = "#d7a25c";
const GOLD_BRIGHT = "#f2c57d";
const MUTED = "#8490a1";
const LINE = "rgba(236,228,209,.18)";
const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = 'Manrope, "Segoe UI", system-ui, sans-serif';

const shade = value => (value <= 1 / 3 ? GOLD_BRIGHT : value <= 2 / 3 ? GOLD : "#7d5f34");

function drawPath(ctx, { mechanic, settled, config = {} }, box) {
  const { x, y, size } = box;
  ctx.save();
  ctx.strokeStyle = "rgba(215,162,92,.35)";
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, size, size);
  const pad = 22;
  const inner = size - pad * 2;
  const ox = x + pad, oy = y + pad;
  const gap = 8;
  if (mechanic === "rings") {
    const count = config.count || settled.length;
    const cx = ox + inner / 2, cy = oy + inner / 2;
    settled.forEach((value, index) => {
      ctx.beginPath();
      ctx.arc(cx, cy, inner / 2 * (1 - index / count), 0, Math.PI * 2);
      ctx.fillStyle = shade(value);
      ctx.fill();
      ctx.strokeStyle = INK;
      ctx.lineWidth = 6;
      ctx.stroke();
    });
  } else if (mechanic === "layers") {
    const bands = config.bands || settled.length;
    const h = (inner - gap * (bands - 1)) / bands;
    settled.forEach((value, index) => { ctx.fillStyle = shade(value); ctx.fillRect(ox, oy + index * (h + gap), inner, h); });
  } else if (mechanic === "sequence") {
    const strips = config.strips || settled.length;
    const w = (inner - gap * (strips - 1)) / strips;
    settled.forEach((value, index) => { ctx.fillStyle = shade(value); ctx.fillRect(ox + index * (w + gap), oy, w, inner); });
  } else {
    const cols = config.cols || Math.round(Math.sqrt(settled.length));
    const cell = (inner - gap * (cols - 1)) / cols;
    settled.forEach((value, index) => {
      const cx = ox + (index % cols) * (cell + gap);
      const cy = oy + Math.floor(index / cols) * (cell + gap);
      ctx.fillStyle = value === null ? "rgba(255,255,255,.06)" : shade(value);
      ctx.fillRect(cx, cy, cell, cell);
    });
  }
  ctx.restore();
}

function drawMark(ctx, x, y, size) {
  ctx.save();
  ctx.strokeStyle = PAPER;
  ctx.lineWidth = size * .06;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size * .43, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + size * .12, y + size / 2);
  ctx.lineTo(x + size * .88, y + size / 2);
  ctx.moveTo(x + size / 2, y + size * .07);
  ctx.bezierCurveTo(x + size * .78, y + size * .35, x + size * .78, y + size * .65, x + size / 2, y + size * .93);
  ctx.bezierCurveTo(x + size * .22, y + size * .65, x + size * .22, y + size * .35, x + size / 2, y + size * .07);
  ctx.stroke();
  ctx.restore();
}

function drawFlame(ctx, x, y, size) {
  ctx.save();
  ctx.strokeStyle = GOLD_BRIGHT;
  ctx.lineWidth = size * .08;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x + size * .5, y);
  ctx.bezierCurveTo(x + size * .62, y + size * .3, x + size, y + size * .45, x + size, y + size * .7);
  ctx.bezierCurveTo(x + size, y + size * 1.05, x, y + size * 1.05, x, y + size * .7);
  ctx.bezierCurveTo(x, y + size * .5, x + size * .2, y + size * .42, x + size * .28, y + size * .35);
  ctx.bezierCurveTo(x + size * .32, y + size * .5, x + size * .42, y + size * .55, x + size * .45, y + size * .5);
  ctx.bezierCurveTo(x + size * .42, y + size * .3, x + size * .48, y + size * .15, x + size * .5, y);
  ctx.stroke();
  ctx.restore();
}

/**
 * @returns {Promise<Blob>} PNG blob of the card
 */
export async function renderShareCard({ mechanic, settled, config, texts }) {
  try { await document.fonts.load(`500 60px ${SERIF}`); await document.fonts.load(`600 30px ${SANS}`); } catch (_) {}
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(215,162,92,.4)";
  ctx.lineWidth = 3;
  ctx.strokeRect(24, 24, W - 48, H - 48);

  // Header
  drawMark(ctx, 80, 78, 56);
  ctx.fillStyle = PAPER;
  ctx.font = `500 40px ${SERIF}`;
  ctx.textBaseline = "middle";
  ctx.fillText("Atlas", 156, 106);
  const atlasWidth = ctx.measureText("Atlas ").width;
  ctx.fillStyle = GOLD;
  ctx.font = `italic 500 40px ${SERIF}`;
  ctx.fillText("of", 156 + atlasWidth, 106);
  const ofWidth = ctx.measureText("of ").width;
  ctx.fillStyle = PAPER;
  ctx.font = `500 40px ${SERIF}`;
  ctx.fillText("Lost Art", 156 + atlasWidth + ofWidth, 106);
  ctx.textAlign = "right";
  ctx.fillStyle = GOLD;
  ctx.font = `700 26px ${SANS}`;
  ctx.fillText(texts.day.toUpperCase(), W - 80, 106);
  ctx.textAlign = "left";

  // Kicker
  ctx.fillStyle = GOLD;
  ctx.font = `700 26px ${SANS}`;
  ctx.fillText(texts.kicker.toUpperCase(), 80, 250);

  // Restoration path
  drawPath(ctx, { mechanic, settled, config }, { x: 80, y: 300, size: 420 });

  // Stats
  const sx = 560;
  ctx.fillStyle = PAPER;
  ctx.font = `500 132px ${SERIF}`;
  ctx.fillText(texts.time, sx, 390);
  ctx.fillStyle = "rgba(242,238,228,.82)";
  ctx.font = `500 34px ${SANS}`;
  ctx.fillText(texts.hints, sx, 490);
  ctx.fillText(texts.moves, sx, 545);
  if (texts.rank) {
    ctx.fillStyle = GOLD_BRIGHT;
    ctx.font = `600 34px ${SANS}`;
    ctx.fillText(texts.rank, sx, 630);
  }

  // Legend
  const ly = 800;
  [[GOLD_BRIGHT, texts.legendFast], [GOLD, texts.legendMid], ["#7d5f34", texts.legendSlow]].forEach(([color, label], index) => {
    const lx = 80 + index * 310;
    ctx.fillStyle = color;
    ctx.fillRect(lx, ly - 12, 26, 26);
    ctx.fillStyle = MUTED;
    ctx.font = `500 24px ${SANS}`;
    ctx.fillText(label, lx + 40, ly);
  });

  // Streak + museum
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(80, 880); ctx.lineTo(W - 80, 880); ctx.stroke();
  drawFlame(ctx, 80, 930, 44);
  ctx.fillStyle = "rgba(242,238,228,.9)";
  ctx.font = `600 32px ${SANS}`;
  ctx.fillText(texts.streak, 150, 958);
  ctx.fillStyle = MUTED;
  ctx.font = `500 28px ${SANS}`;
  ctx.fillText(texts.museum, 150, 1010);

  // Footer
  ctx.beginPath(); ctx.moveTo(80, 1120); ctx.lineTo(W - 80, 1120); ctx.stroke();
  ctx.fillStyle = "rgba(242,238,228,.75)";
  ctx.font = `italic 500 36px ${SERIF}`;
  ctx.fillText(texts.tease, 80, 1200);
  ctx.fillStyle = GOLD;
  ctx.font = `600 28px ${SANS}`;
  ctx.fillText("lostartatlas.com", 80, 1258);

  return new Promise(resolve => canvas.toBlob(resolve, "image/png"));
}
